/**
 * PreviewCardSE — "How it looks in the library" preview for a
 * Series. Mirrors TheTappingSolutionWeb's `SeriesCardVariable`:
 *
 *   • Full-bleed background image
 *   • Two-layer overlay (bottom-up black gradient + half-height
 *     gray fade, same recipe as SubcategoryCardVariable)
 *   • New ribbon top-right when `series_is_new`
 *   • CENTERED title cluster: "{N} day" / Title / "Series"
 *   • "N sessions" at bottom-left, ArrowButton bottom-right
 *
 * Series don't carry "days" as a first-class field — the in-app
 * value is the count of distinct days across the sortable list.
 * Approximate it as the sessions count when not otherwise known.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

function ArrowCircle({ size = 32 }) {
  return (
    <span style={{ position: 'relative', width: size, height: size, display: 'inline-block' }}>
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <ellipse cx="16" cy="16" rx="16" ry="16" fill="rgb(255, 255, 255)" fillOpacity="0.5" />
      </svg>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FFFFFF',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M9.29 6.71a1 1 0 0 0 0 1.41L13.17 12l-3.88 3.88a1 1 0 1 0 1.42 1.41l4.59-4.59a1 1 0 0 0 0-1.41L10.71 6.71a1 1 0 0 0-1.42 0z" />
        </svg>
      </span>
    </span>
  );
}

export default function PreviewCardSE({ state, sessionCount, dayCount }) {
  const imageUrl = state.series_image_preview || state.series_image || '';
  const title = state.series_title || 'Untitled series';
  const isNew = !!state.series_is_new;
  const count = Number(sessionCount) || 0;
  const days = Number(dayCount) || count; // Best-effort

  const universalLink = state.universal_link || null;
  const previewPath = universalLink
    ? universalLink.replace(/^https?:\/\//, '')
    : '— not yet generated —';

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the library"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 240,
            aspectRatio: '216 / 268',
            borderRadius: 14,
            overflow: 'hidden',
            position: 'relative',
            backgroundImage: imageUrl ? `url("${imageUrl}")` : 'none',
            backgroundColor: imageUrl ? 'transparent' : '#2C3E50',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            boxShadow: '0 8px 20px #2C3E5025, 0 1px 0 #FFFFFF80 inset',
          }}
        >
          {/* Bottom-to-top black gradient */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          {/* Half-height gray gradient */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '50%',
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 100%)',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />

          {/* New ribbon */}
          {isNew && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 70,
                height: 70,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  position: 'absolute',
                  top: 11,
                  right: -26,
                  transform: 'rotate(45deg)',
                  background: 'linear-gradient(135deg, #FFC107, #F5A623)',
                  color: '#2C3E50',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  padding: '4px 28px',
                  boxShadow: '0 2px 6px #00000028',
                }}
              >
                New
              </span>
            </span>
          )}

          {/* Centered title cluster */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 14px',
              textAlign: 'center',
              color: '#FFFFFF',
              textShadow: '0 1px 4px #00000060',
              zIndex: 3,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                fontFamily: '"Sofia Pro", "Asap", sans-serif',
                fontSize: 13,
                lineHeight: 1.3,
                color: '#FFFFFFE0',
              }}
            >
              {days > 0 ? `${days} day${days === 1 ? '' : 's'}` : 'New series'}
            </div>
            <h2
              style={{
                margin: '4px 0 4px',
                fontFamily: 'Asap, sans-serif',
                fontSize: 17,
                fontWeight: 500,
                lineHeight: 1.2,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {title}
            </h2>
            <div
              style={{
                fontFamily: '"Sofia Pro", "Asap", sans-serif',
                fontSize: 13,
                color: '#FFFFFFE0',
              }}
            >
              Series
            </div>
          </div>

          {/* Sessions count bottom-left */}
          <div
            style={{
              position: 'absolute',
              bottom: 14,
              left: 14,
              right: 60,
              fontSize: 12,
              color: '#FFFFFFCC',
              textShadow: '0 1px 3px #00000060',
              zIndex: 3,
            }}
          >
            {count} session{count === 1 ? '' : 's'}
          </div>

          {/* Arrow button bottom-right */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 4,
            }}
          >
            <ArrowCircle size={30} />
          </div>
        </div>
      </div>

      {/* preview-foot */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF',
          background: '#FAF4EA',
          fontSize: 11,
          gap: 8,
        }}
      >
        <span
          className="font-mono text-text-muted"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewPath}
        </span>
        {universalLink && (
          <a
            href={universalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="font-condensed font-bold uppercase no-underline"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Open ↗
          </a>
        )}
      </div>
    </RailCard>
  );
}
